/**
 * Pipeline DAG Dedup — Airflow/Dagster duplication finder admin page.
 *
 * Paste a JSON snapshot from `airflow dags list-jobs --output json` or
 * Dagster's GraphQL `pipelineRuns` query. We compute Jaccard similarity
 * across task-shape AND lineage signals and return ranked dedup clusters.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { GitMerge, GitBranch, AlertOctagon, ChevronRight } from 'lucide-react';
import PipelineConnectorWizard from '../../components/PipelineConnectorWizard';

const API = process.env.REACT_APP_BACKEND_URL;

const SAMPLE_INPUT = {
  self_hosted: false,
  pipelines: [
    {
      name: 'customer_etl_v1',
      platform: 'airflow',
      schedule: '@daily',
      task_operator_types: ['BashOperator', 'S3CopyOperator', 'PythonOperator', 'SnowflakeOperator'],
      input_tables: ['raw.customers', 'raw.events'],
      output_tables: ['prod.customer_dim'],
      monthly_runs: 30,
      avg_run_duration_min: 18,
      task_count: 6,
      last_modified_at: '2024-09-12T00:00:00Z',
      owner: 'data-eng',
    },
    {
      name: 'customer_etl_v2_test',
      platform: 'airflow',
      schedule: '@daily',
      task_operator_types: ['BashOperator', 'S3CopyOperator', 'PythonOperator', 'SnowflakeOperator', 'EmailOperator'],
      input_tables: ['raw.customers', 'raw.events'],
      output_tables: ['prod.customer_dim'],
      monthly_runs: 30,
      avg_run_duration_min: 22,
      task_count: 7,
      last_modified_at: '2024-11-04T00:00:00Z',
      owner: 'data-eng',
    },
    {
      name: 'customer_etl_FINAL',
      platform: 'airflow',
      schedule: '@daily',
      task_operator_types: ['BashOperator', 'S3CopyOperator', 'PythonOperator', 'SnowflakeOperator'],
      input_tables: ['raw.customers', 'raw.events', 'raw.subscriptions'],
      output_tables: ['prod.customer_dim'],
      monthly_runs: 30,
      avg_run_duration_min: 20,
      task_count: 6,
      last_modified_at: '2026-02-10T00:00:00Z',
      owner: 'platform',
    },
    {
      name: 'product_analytics_etl',
      platform: 'airflow',
      schedule: '@hourly',
      task_operator_types: ['BashOperator', 'PythonOperator', 'BigQueryOperator'],
      input_tables: ['raw.events', 'raw.sessions'],
      output_tables: ['analytics.daily_metrics'],
      monthly_runs: 720,
      avg_run_duration_min: 4,
      task_count: 3,
      last_modified_at: '2026-01-08T00:00:00Z',
      owner: 'product',
    },
  ],
};

const SeverityBadge = ({ severity }) => {
  const map = {
    high: 'bg-rose-500/15 text-rose-300',
    medium: 'bg-amber-500/15 text-amber-300',
    low: 'bg-emerald-500/15 text-cyan-300',
  };
  return <span className={`inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${map[severity] || map.low}`}>{severity}</span>;
};

export default function PipelineDedup() {
  const [pricing, setPricing] = useState(null);
  const [input, setInput] = useState(JSON.stringify(SAMPLE_INPUT, null, 2));
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/pipeline-dedup/pricing`).then(r => r.json()).then(setPricing).catch(() => null);
  }, []);

  const runScan = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const parsed = JSON.parse(input);
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/pipeline-dedup/scan`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setResult(await r.json());
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px"><div className="max-w-6xl mx-auto px-6 py-8" data-testid="pipeline-dedup-page">
      <div className="mb-10">
        <div className="flex items-start gap-4 mb-4">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-500 bg-gradient-to-br from-emerald-500 to-cyan-500 items-center justify-center shadow-xl shadow-emerald-500/40 shrink-0">
            <GitMerge className="w-7 h-7 text-white drop-shadow-sm" />
          </div>
          <div className="flex-1">
            <Badge className="bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[10px] font-mono tracking-widest mb-2">WORLD-FIRST</Badge>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 drop-shadow-[0_2px_18px_rgba(16,185,129,0.25)]">
              <span className="text-white">Pipeline </span><span className="text-emerald-400">DAG Dedup</span>
            </h1>
          </div>
        </div>
        <p className="text-lg text-slate-600 max-w-3xl leading-relaxed">
          Find duplicated Airflow/Dagster pipelines via <span className="text-emerald-300 font-semibold">task-shape Jaccard + lineage Jaccard</span>. We rank dedup clusters by severity and tell you which DAG to keep canonical. <span className="text-slate-900 font-semibold">No other FinOps vendor crawls this.</span>
        </p>
      </div>

      {/* One-click connector wizard — preferred path */}
      <div className="mb-6">
        <PipelineConnectorWizard onScanResult={setResult} />
      </div>

      <Card className="bg-slate-950 text-slate-100 border-emerald-500/30 mb-6">
        <CardHeader><CardTitle className="text-base text-white">Paste your pipeline inventory (manual fallback)</CardTitle></CardHeader>
        <CardContent>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            spellCheck="false"
            data-testid="pipeline-input"
            className="w-full h-80 bg-slate-950 border border-slate-700 rounded-md p-3 font-mono text-xs text-slate-200"
          />
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-slate-400">Format: <code className="text-cyan-300">pipelines[]</code> with <code className="text-cyan-300">name</code>, <code className="text-cyan-300">task_operator_types</code>, <code className="text-cyan-300">input_tables/output_tables</code> (optional), <code className="text-cyan-300">monthly_runs</code>, <code className="text-cyan-300">avg_run_duration_min</code>, <code className="text-cyan-300">task_count</code>.</p>
            <Button onClick={runScan} disabled={busy} className="bg-cyan-600 hover:bg-emerald-500 text-white" data-testid="run-dedup-btn">
              {busy ? 'Analyzing…' : 'Find duplicates'}
            </Button>
          </div>
          {error && <div className="mt-3 text-sm text-rose-300" data-testid="dedup-error">{error}</div>}
        </CardContent>
      </Card>

      {result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="dedup-summary-cards">
            <Card className="bg-slate-950 text-slate-100 border-emerald-500/20"><CardContent className="p-4"><div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2">Pipelines analyzed</div><div className="text-3xl font-bold text-white tabular-nums">{result.pipelines_analyzed}</div></CardContent></Card>
            <Card className="bg-slate-950 text-slate-100 border-emerald-500/20"><CardContent className="p-4"><div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2">Duplicate clusters</div><div className="text-3xl font-bold text-white tabular-nums">{result.clusters_count}</div></CardContent></Card>
            <Card className="bg-slate-950 text-slate-100 border-emerald-400/30"><CardContent className="p-4"><div className="text-[10px] font-bold text-emerald-300 uppercase tracking-widest mb-2">Recoverable / mo</div><div className="text-3xl font-bold text-emerald-200 tabular-nums">${result.total_recoverable_monthly_usd}</div></CardContent></Card>
            <Card className="bg-slate-950 text-slate-100 border-emerald-400/40"><CardContent className="p-4"><div className="text-[10px] font-bold text-emerald-300 uppercase tracking-widest mb-2">Annual Recoverable</div><div className="text-3xl font-bold text-emerald-200 tabular-nums">${result.annual_recoverable_usd?.toLocaleString()}</div></CardContent></Card>
          </div>

          {result.clusters_count === 0 ? (
            <Card className="bg-slate-950 text-slate-100 border-emerald-500/20"><CardContent className="p-6 text-center text-slate-400">No duplicate clusters detected. {result.note}</CardContent></Card>
          ) : (
            <Card className="bg-slate-950 text-slate-100 border-emerald-500/20">
              <CardHeader><CardTitle className="text-base text-white flex items-center gap-2"><AlertOctagon className="w-4 h-4 text-cyan-400" /> Duplicate clusters ({result.clusters_count})</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {result.clusters.map((c, i) => (
                    <div key={i} className="rounded-md border border-emerald-500/20 bg-slate-950 p-4">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2">
                          <SeverityBadge severity={c.max_severity} />
                          <span className="text-sm font-bold text-white">{c.all_members.length} pipelines</span>
                        </div>
                        <div className="text-right font-mono text-xs">
                          <div className="text-emerald-300">−${c.estimated_monthly_recoverable_usd}/mo</div>
                          <div className="text-slate-400">total: ${c.cluster_monthly_cost_usd}/mo</div>
                        </div>
                      </div>
                      <div className="text-sm text-slate-400 mb-2">
                        <span className="text-emerald-400 font-bold">KEEP:</span>{' '}
                        <span className="font-mono text-white">{c.primary}</span>{' '}
                        <span className="text-slate-400">(${c.primary_monthly_cost_usd}/mo)</span>
                      </div>
                      <div className="text-xs text-slate-400">
                        <span className="text-rose-400 font-bold">DELETE:</span>{' '}
                        {c.duplicate_costs.map((d, j) => (
                          <span key={j} className="font-mono text-slate-300 mr-3">
                            {d.name} <span className="text-rose-300/70">−${d.monthly_cost_usd}/mo</span>
                          </span>
                        ))}
                      </div>
                      <div className="text-xs text-slate-400 mt-3 pt-3 border-t border-slate-800">
                        <div className="font-bold text-slate-400 mb-1 uppercase tracking-widest text-[10px]">Evidence</div>
                        {c.pair_evidence.slice(0, 3).map((e, j) => (
                          <div key={j} className="flex items-center gap-2 mb-0.5">
                            <ChevronRight className="w-3 h-3 text-slate-400" />
                            <span className="text-slate-400">{e.pair.join(' ↔ ')}</span>
                            <span className="text-slate-300">task: {(e.task_jaccard * 100).toFixed(0)}% · lineage: {(e.lineage_jaccard * 100).toFixed(0)}% → combined: <span className="text-white">{(e.combined_jaccard * 100).toFixed(0)}%</span></span>
                          </div>
                        ))}
                      </div>
                      <div className="text-xs text-cyan-300/80 mt-3 italic">{c.recommendation}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {pricing && (
        <Card className="bg-slate-950 text-slate-100 border-slate-700/40 mt-8">
          <CardHeader><CardTitle className="text-sm text-slate-400 flex items-center gap-2"><GitBranch className="w-3 h-3" /> Pricing reference (Feb 2026)</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-xs text-slate-400">
            <div><span className="text-cyan-300">Airflow MWAA:</span> ${pricing.airflow_mwaa_usd_per_task_hour}/task-hour (conservative)</div>
            <div><span className="text-cyan-300">Dagster Cloud Hybrid:</span> ${pricing.dagster_cloud_usd_per_run_hour}/run-hour</div>
            <div><span className="text-cyan-300">Self-hosted floor:</span> ${pricing.self_hosted_usd_per_task_hour}/task-hour</div>
            <div className="text-slate-400 mt-2">Severity thresholds (Jaccard): high ≥{pricing.severity_thresholds.high} · medium ≥{pricing.severity_thresholds.medium} · low ≥{pricing.severity_thresholds.low}</div>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
}
